import { useEffect, useId, useRef, useState } from 'react';
import { Button, LinkButton } from '../../ui';
import { useLocale, useStrings } from '../../state/useLocale';
import {
  duePractice, practiceLesson, readPractice, recordPractice, reviewToOffer, savePractice,
  type PracticeLesson, type PracticeRecord, type PracticeStage,
} from '../../lib/learning-practice';
import type { PublicMission } from '../../types';

export function LearningPractice({ mission, userId, cleared, requestTransfer, clearedAt }: {
  mission: PublicMission; userId: string | null; cleared: boolean; requestTransfer: number;
  clearedAt: (mission: string) => number | null;
}) {
  const lesson = practiceLesson(mission);
  return lesson ? <PracticePanel key={`${userId ?? 'guest'}:${mission.id}`} lesson={lesson}
    userId={userId} cleared={cleared} requestTransfer={requestTransfer} clearedAt={clearedAt} /> : null;
}

/**
 * One due recall case from an earlier level, offered as this level opens.
 * Spaced (a week on) and mixed (another chapter first). It is a link, not a
 * gate: the case opens on its own level, and nothing here blocks this one.
 */
export function ReviewOffer({ mission, missions, userId, clearedAt }: {
  mission: PublicMission; missions: readonly PublicMission[]; userId: string | null;
  clearedAt: (mission: string) => number | null;
}) {
  const s = useStrings().learning;
  const { locale } = useLocale();
  const [record] = useState(() => readPractice(userId));
  if (mission.track !== 'general') return null;
  const worldOf = (id: string) => missions.find((m) => m.id === id)?.world;
  const offer = reviewToOffer(duePractice(record, Date.now(), clearedAt), mission, worldOf);
  if (!offer) return null;
  return <aside className="learning-review learning-review--offer" aria-label={s.due}>
    <h3>{s.offerTitle(offer.level)}</h3>
    <p>{s.offerNote}</p>
    <LinkButton variant="quiet" href={`?mission=${encodeURIComponent(offer.mission)}&practice=review`}>
      {s.revisit}: {offer.text[locale].title}
    </LinkButton>
  </aside>;
}

function PracticePanel({ lesson, userId, cleared, requestTransfer, clearedAt }: {
  lesson: PracticeLesson; userId: string | null; cleared: boolean; requestTransfer: number;
  clearedAt: (mission: string) => number | null;
}) {
  const { locale } = useLocale();
  const s = useStrings().learning;
  const words = lesson.text[locale];
  const [record, setRecord] = useState(() => readPractice(userId));
  const reviewDue = duePractice(record, Date.now(), clearedAt).some((entry) => entry.mission === lesson.mission);
  const reviewRequested = new URLSearchParams(window.location.search).get('practice') === 'review';
  const [stage, setStage] = useState<PracticeStage>(() => reviewRequested && reviewDue ? 'review' : 'predict');
  const [open, setOpen] = useState(reviewRequested || !record[lesson.mission]?.predict);
  const panel = useRef<HTMLDetailsElement>(null);
  const transferAvailable = cleared || Boolean(record[lesson.mission]?.transfer);
  const reviewAvailable = reviewDue || Boolean(record[lesson.mission]?.review);

  useEffect(() => {
    if (!requestTransfer) return;
    setStage('transfer');
    setOpen(true);
    // ClearOverlay restores focus as it leaves; focus after that cleanup.
    const frame = requestAnimationFrame(() => {
      panel.current?.querySelector('summary')?.focus();
      panel.current?.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [requestTransfer]);

  return <details className="learning-practice" ref={panel} open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
    <summary>{s.title}</summary>
    <h3>{words.title}</h3>
    <p>{s.intro}</p>
    <div className="learning-stages" aria-label={s.title}>
      {(['predict', 'transfer', 'review'] as const).map((item) => <Button key={item} variant="quiet"
        disabled={item === 'transfer' ? !transferAvailable : item === 'review' ? !reviewAvailable : false}
        aria-pressed={stage === item} onClick={() => setStage(item)}>{s[item]}</Button>)}
    </div>
    {stage === 'predict' ? <p>{words.lesson}</p> : <details><summary>{s.lesson}</summary><p>{words.lesson}</p></details>}
    <PracticeQuestion key={stage} lesson={lesson} stage={stage} record={record} userId={userId} onRecord={setRecord} />
    {!transferAvailable && <p className="note">{s.locked}</p>}
    {!reviewAvailable && <p className="note">{s.later}</p>}
  </details>;
}

function PracticeQuestion({ lesson, stage, record, userId, onRecord }: {
  lesson: PracticeLesson; stage: PracticeStage; record: PracticeRecord; userId: string | null;
  onRecord: (record: PracticeRecord) => void;
}) {
  const s = useStrings().learning;
  const { locale } = useLocale();
  const words = lesson.text[locale];
  const exercise = lesson.cases[stage];
  const id = useId();
  const [output, setOutput] = useState('');
  const [predictionReady, setPredictionReady] = useState(!exercise.proposal);
  const [decision, setDecision] = useState<'accept' | 'revise' | null>(null);
  const proposalChoices = useRef<HTMLFieldSetElement>(null);
  const [choice, setChoice] = useState<number | null>(null);
  const [explanation, setExplanation] = useState('');
  const [aided, setAided] = useState(false);
  const [result, setResult] = useState<{ correct: boolean; saved: boolean } | null>(null);
  const first = record[lesson.mission]?.[stage];

  useEffect(() => {
    if (!exercise.proposal || !predictionReady) return;
    proposalChoices.current?.querySelector('input')?.focus();
  }, [exercise.proposal, predictionReady]);

  return <form onSubmit={(event) => {
    event.preventDefault();
    if (choice === null || !output.trim() || !explanation.trim() || !predictionReady || (exercise.proposal && decision === null)) return;
    const outputCorrect = output.trim() === exercise.answer ||
      (/^-?\d+(\.\d+)?$/.test(output.trim()) && Number(output) === Number(exercise.answer));
    const evidenceCorrect = choice === exercise.correctEvidence;
    const proposalCorrect = exercise.proposal ? decision === exercise.proposal.decision : undefined;
    const next = recordPractice(record, lesson.mission, stage, { at: Date.now(), outputCorrect, evidenceCorrect, aided,
      ...(proposalCorrect === undefined ? {} : { proposalCorrect }) });
    const saved = savePractice(userId, next);
    onRecord(next);
    setResult({ correct: outputCorrect && evidenceCorrect && proposalCorrect !== false, saved });
  }}>
    <pre dir="ltr"><code>{exercise.code}</code></pre>
    <label htmlFor={`${id}-output`}>{s.output}</label>
    <input id={`${id}-output`} dir="ltr" value={output} onChange={(event) => setOutput(event.target.value)} required
      autoComplete="off" disabled={result !== null || Boolean(exercise.proposal && predictionReady)} />
    {exercise.proposal && !predictionReady && <>
      <p>{s.predictBeforeReview}</p>
      <Button type="button" disabled={!output.trim()} onClick={() => setPredictionReady(true)}>{s.openProposal}</Button>
    </>}
    {exercise.proposal && predictionReady && <>
      <p>{words.task}</p>
      <h4>{s.proposal}</h4>
      <pre dir="ltr"><code>{exercise.proposal.code}</code></pre>
      <fieldset ref={proposalChoices} disabled={result !== null}>
        <legend>{s.decision}</legend>
        {(['accept', 'revise'] as const).map((item) => <label className="learning-choice" key={item}>
          <input type="radio" name={`${id}-decision`} checked={decision === item} required
            onChange={() => setDecision(item)} />{s[item]}
        </label>)}
      </fieldset>
    </>}
    <fieldset disabled={result !== null || !predictionReady}>
      <legend>{words.task}</legend>
      {exercise.evidence.map((evidence, index) => <label className="learning-choice" key={evidence}>
        <input type="radio" name={`${id}-evidence`} checked={choice === index} required
          onChange={() => setChoice(index)} /> <code dir="ltr">{evidence}</code>
      </label>)}
    </fieldset>
    <label htmlFor={`${id}-explain`}>{exercise.proposal ? s.explainProposal : s.explain}</label>
    <textarea id={`${id}-explain`} value={explanation} required disabled={result !== null || !predictionReady}
      aria-describedby={`${id}-privacy`} onChange={(event) => setExplanation(event.target.value)} />
    <p className="note" id={`${id}-privacy`}>{s.explainNote}</p>
    <label className="learning-choice"><input type="checkbox" checked={aided} disabled={result !== null}
      onChange={(event) => setAided(event.target.checked)} />{s.aided}</label>
    {first && !result && <p className="note">{s.recorded}</p>}
    {!result && <Button type="submit" variant="solid" disabled={!predictionReady}>{s.submit}</Button>}
    {result && <div className="learning-feedback" role="status">
      <strong>{result.correct ? exercise.proposal ? s.reviewCorrect : s.correct : s.retry}</strong>
      <p>{s.answer}: <code dir="ltr">{exercise.answer}</code></p>
      <p>{s.proof}: <code dir="ltr">{exercise.evidence[exercise.correctEvidence]}</code></p>
      <p>{words.reason}</p><p>{s.compare}</p>
      {exercise.proposal && <>
        <p>{s.reviewAnswer}: {s[exercise.proposal.decision]}</p>
        <p>{exercise.proposal.reason[locale]}</p>
        <p>{s.applyReview}</p>
      </>}
      <p className="note">{s.first} {result.saved ? s.saved : s.unsaved}</p>
      {first && <p className="note">{s.observed}: {first.aided ? s.assisted : s.independent} · {s.outputResult}: {first.outputCorrect ? s.yes : s.no} · {s.evidenceResult}: {first.evidenceCorrect ? s.yes : s.no}</p>}
      {first?.proposalCorrect !== undefined && <p className="note">{s.reviewResult}: {first.proposalCorrect ? s.yes : s.no}</p>}
    </div>}
  </form>;
}
